'use client'

import * as React from 'react'
import * as Select from '@radix-ui/react-select'
import { ChevronDown, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/lib/store'
import { COUNTRIES } from '@/lib/constants'
import type { Country } from '@/lib/types'

interface CountrySelectorProps {
  variant?: 'default' | 'icon'
}

export function CountrySelector({ variant = 'default' }: CountrySelectorProps) {
  const { country, setCountry } = useUIStore()
  
  const handleChange = (value: Country) => {
    const countryOption = COUNTRIES.find((c) => c.code === value)
    if (countryOption?.enabled) {
      setCountry(value)
    }
  }
  
  const selectedCountry = COUNTRIES.find((c) => c.code === country)
  
  return (
    <Select.Root value={country} onValueChange={handleChange}>
      <Select.Trigger
        className={cn(
          'inline-flex items-center justify-center rounded-full',
          'transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-ring',
          variant === 'icon' ? [
            'h-10 w-10',
            'hover:bg-muted',
          ] : [
            'gap-2 px-3 py-2',
            'bg-card border border-border',
            'hover:bg-muted',
          ]
        )}
        title={selectedCountry?.name}
      >
        <span className={cn(variant === 'icon' ? 'text-xl' : 'text-lg')}>
          {getCountryFlag(country)}
        </span>
        {variant === 'default' && (
          <>
            <Select.Value>
              <span className="hidden sm:inline text-sm font-medium">{selectedCountry?.code}</span>
            </Select.Value>
            <Select.Icon>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </Select.Icon>
          </>
        )}
      </Select.Trigger>
      
      <Select.Portal>
        <Select.Content
          className={cn(
            'overflow-hidden rounded-lg',
            'bg-popover border border-border shadow-lg',
            'animate-in fade-in-0 zoom-in-95',
            'z-50'
          )}
          position="popper"
          sideOffset={4}
          align="end"
        >
          <Select.Viewport className="p-1">
            {COUNTRIES.map((countryOption) => (
              <Select.Item
                key={countryOption.code}
                value={countryOption.code}
                disabled={!countryOption.enabled}
                className={cn(
                  'relative flex items-center gap-2 px-8 py-2 text-sm rounded-md',
                  'cursor-pointer select-none outline-none',
                  'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
                  !countryOption.enabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                <Select.ItemIndicator className="absolute left-2">
                  <Check className="h-4 w-4" />
                </Select.ItemIndicator>
                <span className="text-lg">{getCountryFlag(countryOption.code)}</span>
                <Select.ItemText>{countryOption.name}</Select.ItemText>
                {!countryOption.enabled && (
                  <span className="ml-auto text-xs text-muted-foreground">
                    Coming soon
                  </span>
                )}
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  )
}

function getCountryFlag(code: Country): string {
  const flags: Record<Country, string> = {
    CZ: '🇨🇿',
    DE: '🇩🇪',
    PL: '🇵🇱',
  }
  return flags[code] || '🏳️'
}

